import { memo, useEffect, useState } from 'react';

import { Input, type InputProps, Select } from 'antd';
import cn from 'classnames';

import {
    DEFAULT_PHONE_CODE,
    PHONE_CODES,
} from '@/shared/constants/phone-codes';

import cls from './phone-input.module.css';

interface PhoneInputProps extends Omit<InputProps, 'value' | 'onChange'> {
    isBlock?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

export const PhoneInput = memo((props: PhoneInputProps) => {
    const { isBlock, className, value, onChange, ...inputProps } = props;

    const [phoneCode, setPhoneCode] = useState(DEFAULT_PHONE_CODE);
    const [phoneNumber, setPhoneNumber] = useState('');

    // Parse the full phone number into code and number when value changes
    useEffect(() => {
        if (!value) {
            setPhoneCode(DEFAULT_PHONE_CODE);
            setPhoneNumber('');

            return;
        }

        // Try to extract phone code from the full phone number
        const phoneCodeMatch = PHONE_CODES.find((code) =>
            value.startsWith(code.code),
        );

        if (phoneCodeMatch) {
            setPhoneCode(phoneCodeMatch.code);
            setPhoneNumber(value.slice(phoneCodeMatch.code.length));
        } else {
            // If no code found, assume the whole thing is the number
            setPhoneCode(DEFAULT_PHONE_CODE);
            setPhoneNumber(value);
        }
    }, [value]);

    // Handle phone code changes
    const handlePhoneCodeChange = (newPhoneCode: string) => {
        setPhoneCode(newPhoneCode);
        const fullPhoneNumber = phoneNumber
            ? `${newPhoneCode}${phoneNumber}`
            : '';

        onChange?.(fullPhoneNumber);
    };

    // Handle phone number changes
    const handlePhoneNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newPhoneNumber = event.target.value;

        setPhoneNumber(newPhoneNumber);
        const fullPhoneNumber = newPhoneNumber
            ? `${phoneCode}${newPhoneNumber}`
            : '';

        onChange?.(fullPhoneNumber);
    };

    return (
        <Input.Group
            className={cn(className, cls.root, { [cls.block]: isBlock })}
            compact
        >
            <Select
                className={cls.codeSelect}
                filterOption={(input, option) =>
                    option?.children
                        ?.toString()
                        .toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                }
                onChange={handlePhoneCodeChange}
                optionFilterProp="children"
                showSearch
                style={{ width: '25%' }}
                value={phoneCode}
            >
                {PHONE_CODES.map((item) => (
                    <Select.Option
                        key={item.code}
                        value={item.code}
                    >
                        {item.flag} {item.code}
                    </Select.Option>
                ))}
            </Select>
            <Input
                {...inputProps}
                autoComplete="tel"
                className={cls.numberInput}
                onChange={handlePhoneNumberChange}
                style={{ width: '75%' }}
                type="tel"
                value={phoneNumber}
            />
        </Input.Group>
    );
});

PhoneInput.displayName = 'PhoneInput';
